import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Banner } from '@lumal/ui';
import { apiFetch } from '@lumal/lib';
import { API } from '../../api';
import { btnPrimary, btnSecondary, btnDanger, tbl, badge } from '../../styles';
import type { Ticket, Grant, SupportActionRow, GrantChange } from '../../types';

// SupportPanel: recorrido CSE completo (stakeholders.md §3 punto 5 / RBAC §4.5).
// Loop: tomar ticket → grant con scope (módulos + acciones + dato clínico + TTL),
// ejecutar lecturas de soporte validadas contra el grant, ver la traza de acciones
// y el TTL restante, y cerrar el ticket (expira los grants y notifica al operador).
export function SupportPanel({ csrf }: { csrf: string }) {
  const qc = useQueryClient();
  // El filtro arranca en in_progress + open (un ticket "tomado" pasa a in_progress
  // y no debe desaparecer de la vista por defecto: era parte del bug (a)/(c)).
  const [statusFilter, setStatusFilter] = useState<'open' | 'in_progress' | 'open_active' | 'closed' | 'all'>('open_active');
  const [grantedFor, setGrantedFor] = useState<Record<string, Grant>>({});
  const [scopeFor, setScopeFor] = useState<Record<string, { clinical: boolean; ttl: number }>>({});
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [q, setQ] = useState('');

  const tickets = useQuery<{ items: Ticket[] }>({
    queryKey: ['tickets', statusFilter],
    queryFn: async () => {
      // 'open_active' = open + in_progress (los accionables); el core filtra por un
      // único status, así que combinamos dos lecturas y deduplicamos.
      if (statusFilter === 'open_active') {
        const [open, prog] = await Promise.all([
          apiFetch<{ items: Ticket[] }>(`${API}/portal/tickets?status=open`),
          apiFetch<{ items: Ticket[] }>(`${API}/portal/tickets?status=in_progress`),
        ]);
        const seen = new Set<string>();
        const items = [...(prog.items || []), ...(open.items || [])].filter((t) =>
          seen.has(t.id) ? false : (seen.add(t.id), true),
        );
        return { items };
      }
      return apiFetch<{ items: Ticket[] }>(
        `${API}/portal/tickets${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`,
      );
    },
  });

  // Rehidratación del estado del grant (bug c): al montar/recargar la SPA, el estado
  // local grantedFor está vacío. Cargamos los grants ACTIVOS del CSE y los indexamos
  // por ticket_id, descartando los ya expirados por TTL. Así un ticket ya tomado se
  // pinta como "tomado" y se puede volver a operar sin re-adquirir.
  const activeGrants = useQuery<{ items: Grant[] }>({
    queryKey: ['active-grants'],
    queryFn: () => apiFetch<{ items: Grant[] }>(`${API}/portal/grants?status=active`),
  });
  useEffect(() => {
    const items = activeGrants.data?.items;
    if (!items) return;
    const now = Date.now();
    const byTicket: Record<string, Grant> = {};
    for (const g of items) {
      if (g.status !== 'active') continue;
      if (new Date(g.expires_at).getTime() <= now) continue;
      // El primero gana (la query viene ordenada por granted_at DESC = más reciente).
      if (!byTicket[g.ticket_id]) byTicket[g.ticket_id] = g;
    }
    setGrantedFor((prev) => ({ ...byTicket, ...prev }));
  }, [activeGrants.data]);

  const acquire = useMutation({
    mutationFn: async (vars: { ticket: Ticket; clinical: boolean; ttl: number }): Promise<Grant> => {
      try {
        const resp = await apiFetch<{ grant: Grant }>(`${API}/portal/grants`, 'POST', {
          headers: { 'X-CSRF-Token': csrf },
          body: {
            ticket_id: vars.ticket.id,
            // El módulo del ticket llega del formulario del operador en ESPAÑOL
            // (agenda/imagen/…) pero el enforcement y "tomar control" usan la
            // taxonomía canónica en INGLÉS (scheduling/clinical/…). Normalizamos aquí
            // para que el grant nazca con módulos operables (el core re-normaliza por
            // si llega de otro cliente; este mapeo deja el UI coherente desde el
            // primer render). Sin esto, scope_modules=["agenda"] nunca empata
            // op.module=="scheduling" → "el scope no habilita operaciones de control".
            scope_modules: ticketModulesToScope(vars.ticket.module),
            // Scope de acciones útil por defecto: tomar el ticket habilita tanto la
            // lectura de soporte como las operaciones de "tomar control" (bug b). El
            // CSE puede recortarlo luego en "Ajustar grant".
            scope_actions: DEFAULT_GRANT_ACTIONS,
            clinical_data_access: vars.clinical,
            ttl_hours: vars.ttl,
          },
        });
        return resp.grant;
      } catch (e) {
        // 409 GRANT_EXISTS: el ticket ya estaba tomado por este CSE (p.ej. tras
        // recargar). El core devuelve el grant existente → lo re-adoptamos en vez de
        // fallar, evitando el "no deja re-tomar" del bug (c).
        const err = e as { code?: string; grant?: Grant };
        if (err.code === 'GRANT_EXISTS' && err.grant) return err.grant;
        throw e;
      }
    },
    onSuccess: (grant) => {
      setGrantedFor((prev) => ({ ...prev, [grant.ticket_id]: grant }));
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['active-grants'] });
    },
  });

  const close = useMutation({
    mutationFn: (ticket: Ticket) =>
      apiFetch(`${API}/portal/tickets/${ticket.id}/close`, 'POST', {
        headers: { 'X-CSRF-Token': csrf },
        body: {},
      }),
    onSuccess: (_d, ticket) => {
      setGrantedFor((prev) => {
        const next = { ...prev };
        delete next[ticket.id];
        return next;
      });
      if (selected?.id === ticket.id) setSelected(null);
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['active-grants'] });
    },
  });

  const scopeOf = (t: Ticket) => scopeFor[t.id] ?? { clinical: t.clinical_data_access, ttl: 4 };

  return (
    <section data-testid="cse-panel" style={{ marginTop: '1rem' }}>
      <div style={{ marginBottom: '0.75rem' }}>
        <label>
          Filtro:{' '}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} data-testid="status-filter">
            <option value="open_active">Open + en curso</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </label>
        {' '}
        <input placeholder="Buscar por ID, tenant o descripción…" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="ticket-search"
          style={{ marginLeft: 8, minWidth: 260 }} />
      </div>

      {tickets.isLoading ? <p>Cargando tickets…</p> : null}
      {tickets.data?.items?.length === 0 ? <Banner variant="info">No hay tickets con ese filtro.</Banner> : null}
      {acquire.isError ? <Banner variant="warn">No se pudo adquirir el grant.</Banner> : null}

      <table style={tbl} data-testid="tickets-table">
        <thead>
          <tr><th>ID</th><th>Tenant</th><th>Módulo</th><th>Descripción</th><th>Severity</th><th>Status</th><th>Grant / acción</th></tr>
        </thead>
        <tbody>
          {(tickets.data?.items || []).filter((t) => {
            const ql = q.trim().toLowerCase();
            return !ql || [t.id, t.tenant_id, t.description, t.module, t.severity].some((f) => String(f ?? '').toLowerCase().includes(ql));
          }).map((t) => {
            const grant = grantedFor[t.id];
            const sc = scopeOf(t);
            const closed = t.status === 'closed';
            return (
              <tr key={t.id} data-testid={`ticket-${t.id}`}>
                <td><code title={t.id} data-testid={`ticket-id-${t.id}`}>{String(t.id).slice(0, 8)}</code></td>
                <td data-testid={`ticket-tenant-${t.id}`}>{t.tenant_id}</td>
                <td>{t.module}</td><td>{t.description}</td>
                <td>{t.severity}</td><td>{t.status}</td>
                <td>
                  {closed ? (
                    <span style={badge}>cerrado</span>
                  ) : !grant ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ fontSize: '0.78rem' }}>
                        <input
                          type="checkbox"
                          checked={sc.clinical}
                          onChange={(e) => setScopeFor((p) => ({ ...p, [t.id]: { ...sc, clinical: e.target.checked } }))}
                          data-testid={`scope-clinical-${t.id}`}
                        />{' '}dato clínico
                      </label>
                      <label style={{ fontSize: '0.78rem' }}>
                        TTL{' '}
                        <select
                          value={sc.ttl}
                          onChange={(e) => setScopeFor((p) => ({ ...p, [t.id]: { ...sc, ttl: Number(e.target.value) } }))}
                          data-testid={`scope-ttl-${t.id}`}
                        >
                          <option value={1}>1h</option>
                          <option value={4}>4h</option>
                          <option value={24}>24h</option>
                          <option value={48}>48h</option>
                        </select>
                      </label>
                      <button
                        style={btnPrimary}
                        onClick={() => acquire.mutate({ ticket: t, clinical: sc.clinical, ttl: sc.ttl })}
                        disabled={acquire.isPending}
                        data-testid={`acquire-${t.id}`}
                      >
                        Tomar ticket (grant)
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--lumal-success)', fontSize: '0.78rem' }} data-testid={`grant-active-${t.id}`}>
                        ✓ grant activo · {grant.clinical_data_access ? 'clínico' : 'no-clínico'}
                      </span>
                      <button style={btnSecondary} onClick={() => setSelected(t)} data-testid={`open-grant-${t.id}`}>
                        Operar
                      </button>
                      <button
                        style={btnSecondary}
                        onClick={() => close.mutate(t)}
                        disabled={close.isPending}
                        data-testid={`close-${t.id}`}
                      >
                        Cerrar ticket
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {(() => {
        const sel = selected;
        const g = sel ? grantedFor[sel.id] : undefined;
        if (!sel || !g) return null;
        return (
          <GrantWorkspace
            key={sel.id}
            csrf={csrf}
            ticket={sel}
            grant={g}
            onClose={() => close.mutate(sel)}
            onGrantUpdate={(updated) =>
              setGrantedFor((prev) => ({ ...prev, [updated.ticket_id]: updated }))
            }
            onRevoked={() => {
              setGrantedFor((prev) => {
                const next = { ...prev };
                delete next[sel.id];
                return next;
              });
              setSelected(null);
              qc.invalidateQueries({ queryKey: ['tickets'] });
              qc.invalidateQueries({ queryKey: ['active-grants'] });
            }}
          />
        );
      })()}
    </section>
  );
}

// Catálogo de scope para los multi-selects de "Ajustar grant" (espeja el del core).
const SCOPE_MODULES = ['patients', 'scheduling', 'encounter', 'laboratory', 'inventory', 'billing', 'clinical'];
const SCOPE_ACTIONS = ['read', 'write', 'transition', 'finalize', 'cancel', 'order', 'result', 'verify', 'issue', 'pay', 'movement'];

// ticketModulesToScope traduce el módulo del ticket (taxonomía del formulario del
// operador, en español) a la taxonomía canónica de scope-módulos (inglés) que usan
// ACT_AS_OPS y el enforcement del core. Espeja ticketModuleToScope del core. Los
// módulos sin op de control directa (o 'general'/'referidor') siembran todo el
// scope canónico para no dejar a soporte sin operaciones. Un valor ya canónico se
// conserva (idempotente).
function ticketModulesToScope(ticketModule: string): string[] {
  const map: Record<string, string[]> = {
    agenda: ['scheduling'],
    consulta: ['encounter'],
    laboratorio: ['laboratory'],
    inventario: ['inventory'],
    facturacion: ['billing'],
    imagen: ['clinical'],
    referidor: SCOPE_MODULES,
    general: SCOPE_MODULES,
  };
  if (SCOPE_MODULES.includes(ticketModule)) return [ticketModule];
  return map[ticketModule] ?? SCOPE_MODULES;
}

// Scope de acciones por defecto al tomar un ticket: además de 'read' (lecturas de
// soporte) incluye las acciones de escritura/control que usan las operaciones de
// "Tomar control", para que estén disponibles out-of-the-box sin tener que ir a
// "Ajustar grant" a habilitar 'write' manualmente (bug b).
const DEFAULT_GRANT_ACTIONS = ['read', 'write', 'transition', 'finalize', 'order', 'result', 'verify', 'issue', 'pay', 'movement'];

// Operaciones representativas para "Tomar control" por módulo (Ola 1). No replica
// toda la UI del operador: sólo las operaciones clave para reproducir/arreglar.
type ActAsOp = {
  label: string;
  module: string;
  action: string;
  method: string;
  path: string;
  clinical?: boolean;
  // payload de demo (mínimo); el CSE lo edita en el textarea.
  samplePayload: Record<string, unknown>;
};
const ACT_AS_OPS: ActAsOp[] = [
  {
    label: 'Scheduling · crear cita',
    module: 'scheduling', action: 'write', method: 'POST',
    path: '/api/internal/v1/scheduling/appointments',
    samplePayload: { resource_id: '', patient_id: '', starts_at: '', ends_at: '' },
  },
  {
    label: 'Inventory · registrar movimiento',
    module: 'inventory', action: 'movement', method: 'POST',
    path: '/api/internal/v1/inventory/movements',
    samplePayload: { item_id: '', branch_id: '', kind: 'in', quantity: 1 },
  },
  {
    label: 'Billing · emitir factura',
    module: 'billing', action: 'issue', method: 'POST',
    path: '/api/internal/v1/billing/invoices',
    samplePayload: { patient_id: '', branch_id: '', lines: [] },
  },
  {
    label: 'Encounter · crear (clínico)',
    module: 'encounter', action: 'write', method: 'POST',
    path: '/api/internal/v1/encounters/',
    clinical: true,
    samplePayload: { patient_id: '', branch_id: '' },
  },
];

// GrantWorkspace: opera bajo un grant activo. Lecturas de soporte con scope,
// ajuste del ciclo de vida del grant (scope/clínico/TTL/revocar + historial),
// "tomar control" (ejecutar operaciones reales del tenant) y la traza de acciones.
function GrantWorkspace({
  csrf,
  ticket,
  grant,
  onClose,
  onGrantUpdate,
  onRevoked,
}: {
  csrf: string;
  ticket: Ticket;
  grant: Grant;
  onClose: () => void;
  onGrantUpdate: (g: Grant) => void;
  onRevoked: () => void;
}) {
  const remaining = useTtlRemaining(grant.expires_at);

  const actions = useQuery<{ items: SupportActionRow[] }>({
    queryKey: ['support-actions', ticket.tenant_id],
    queryFn: () =>
      apiFetch<{ items: SupportActionRow[] }>(`${API}/portal/actions?tenant_id=${encodeURIComponent(ticket.tenant_id)}`),
    refetchInterval: 4000,
  });

  const read = useMutation({
    mutationFn: (kind: 'patients' | 'encounters' | 'branches') =>
      apiFetch<{ items: unknown[]; count?: number }>(
        `${API}/portal/support-data?grant_id=${grant.id}&kind=${kind}`,
      ),
    onSuccess: () => actions.refetch(),
  });

  return (
    <div
      data-testid="grant-workspace"
      style={{
        marginTop: '1rem', padding: '1rem', border: '1px solid var(--lumal-border)',
        borderRadius: 6, background: 'var(--lumal-surface)',
      }}
    >
      {/* Banner de modo elevado: refuerza que se opera como SOPORTE y queda auditado. */}
      <Banner variant="warn">
        <span data-testid="elevated-banner">
          ⚠ Estás operando como SOPORTE sobre el tenant <strong>{ticket.tenant_id}</strong> bajo el ticket{' '}
          {ticket.id.slice(0, 8)} — TTL {remaining}. Toda acción queda auditada con tu identidad (CSE).
        </span>
      </Banner>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: '0.5rem' }}>
        <strong>
          Operando ticket de <span data-testid="ws-tenant">{ticket.tenant_id}</span> · {ticket.module}
        </strong>
        <span style={badge} data-testid="grant-ttl-remaining">
          TTL restante: {remaining}
        </span>
      </div>

      <div style={{ marginTop: '0.75rem', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={btnSecondary} onClick={() => read.mutate('branches')} disabled={read.isPending} data-testid="read-branches">
          Leer sucursales
        </button>
        <button style={btnSecondary} onClick={() => read.mutate('patients')} disabled={read.isPending} data-testid="read-patients">
          Leer pacientes (clínico)
        </button>
        <button style={btnSecondary} onClick={() => read.mutate('encounters')} disabled={read.isPending} data-testid="read-encounters">
          Leer encuentros (clínico)
        </button>
        <button style={btnPrimary} onClick={onClose} data-testid="ws-close">
          Cerrar ticket
        </button>
      </div>

      {read.isError ? (
        <Banner variant="warn">
          Lectura denegada por scope del grant ({(read.error as { code?: string })?.code ?? 'ERROR'}).
        </Banner>
      ) : null}
      {read.data ? (
        <p data-testid="read-result" style={{ fontSize: '0.85rem' }}>
          Última lectura: {read.data.items?.length ?? 0} registro(s).
        </p>
      ) : null}

      <GrantLifecycle csrf={csrf} grant={grant} onGrantUpdate={onGrantUpdate} onRevoked={onRevoked} />

      <TakeControl csrf={csrf} grant={grant} onDone={() => actions.refetch()} />

      <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Traza de acciones del CSE</h4>
      {actions.isLoading ? <p>Cargando traza…</p> : null}
      {actions.data?.items?.length === 0 ? <Banner variant="info">Sin acciones todavía.</Banner> : null}
      <table style={tbl} data-testid="actions-table">
        <thead>
          <tr><th>Cuándo</th><th>CSE</th><th>Acción</th><th>Recurso</th></tr>
        </thead>
        <tbody>
          {(actions.data?.items || []).map((a) => (
            <tr key={a.id} data-testid={`action-${a.id}`}>
              <td>{new Date(a.executed_at).toLocaleString()}</td>
              <td>{a.support_user_id}</td>
              <td>{a.action}</td>
              <td>{a.resource_type}{a.resource_id ? ` · ${a.resource_id}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// GrantLifecycle: ajustar el grant en el tiempo (scope/clínico), extender/acortar
// TTL, revocar, y ver el historial de cambios.
function GrantLifecycle({
  csrf,
  grant,
  onGrantUpdate,
  onRevoked,
}: {
  csrf: string;
  grant: Grant;
  onGrantUpdate: (g: Grant) => void;
  onRevoked: () => void;
}) {
  const [modules, setModules] = useState<string[]>(grant.scope_modules);
  const [acts, setActs] = useState<string[]>(grant.scope_actions);
  const [clinical, setClinical] = useState(grant.clinical_data_access);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const headers = { 'X-CSRF-Token': csrf };

  const modify = useMutation({
    mutationFn: () =>
      apiFetch<{ grant: Grant }>(`${API}/portal/grants/${grant.id}`, 'PATCH', {
        headers,
        body: { scope_modules: modules, scope_actions: acts, clinical_data_access: clinical },
      }),
    onSuccess: (r) => onGrantUpdate(r.grant),
  });

  const extend = useMutation({
    mutationFn: (hours: number) =>
      apiFetch<{ grant: Grant }>(`${API}/portal/grants/${grant.id}/extend`, 'POST', {
        headers,
        body: { ttl_hours: hours },
      }),
    onSuccess: (r) => onGrantUpdate(r.grant),
  });

  const revoke = useMutation({
    mutationFn: () =>
      apiFetch<{ grant: Grant }>(`${API}/portal/grants/${grant.id}/revoke`, 'POST', {
        headers,
        body: { reason: revokeReason },
      }),
    onSuccess: () => onRevoked(),
  });

  const history = useQuery<{ items: GrantChange[] }>({
    queryKey: ['grant-history', grant.id, showHistory],
    queryFn: () => apiFetch<{ items: GrantChange[] }>(`${API}/portal/grants/${grant.id}/history`),
    enabled: showHistory,
  });

  const toggle = (list: string[], v: string, set: (l: string[]) => void) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <div data-testid="grant-lifecycle" style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--lumal-border)' }}>
      <h4 style={{ margin: '0 0 0.5rem' }}>Ajustar grant</h4>

      <div style={{ fontSize: '0.8rem' }}>
        <div style={{ marginBottom: 6 }}>
          <strong>Módulos:</strong>{' '}
          {SCOPE_MODULES.map((m) => (
            <label key={m} style={{ marginRight: 10 }}>
              <input type="checkbox" checked={modules.includes(m)} onChange={() => toggle(modules, m, setModules)} data-testid={`mod-${m}`} /> {m}
            </label>
          ))}
        </div>
        <div style={{ marginBottom: 6 }}>
          <strong>Acciones:</strong>{' '}
          {SCOPE_ACTIONS.map((a) => (
            <label key={a} style={{ marginRight: 10 }}>
              <input type="checkbox" checked={acts.includes(a)} onChange={() => toggle(acts, a, setActs)} data-testid={`act-${a}`} /> {a}
            </label>
          ))}
        </div>
        <label style={{ marginRight: 10 }}>
          <input type="checkbox" checked={clinical} onChange={(e) => setClinical(e.target.checked)} data-testid="lc-clinical" /> dato clínico
        </label>
        <button style={btnSecondary} onClick={() => modify.mutate()} disabled={modify.isPending} data-testid="apply-scope">
          Aplicar scope
        </button>
        {modify.isError ? <Banner variant="warn">No se pudo modificar ({(modify.error as { code?: string })?.code ?? 'ERROR'}).</Banner> : null}
      </div>

      <div style={{ marginTop: '0.6rem', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '0.8rem' }}>TTL:</strong>
        <button style={btnSecondary} onClick={() => extend.mutate(1)} disabled={extend.isPending} data-testid="ttl-plus1">+1h</button>
        <button style={btnSecondary} onClick={() => extend.mutate(4)} disabled={extend.isPending} data-testid="ttl-plus4">+4h</button>
        <button style={btnSecondary} onClick={() => extend.mutate(-1)} disabled={extend.isPending} data-testid="ttl-minus1">−1h</button>
      </div>

      <div style={{ marginTop: '0.6rem' }}>
        {!confirmRevoke ? (
          <button style={btnDanger} onClick={() => setConfirmRevoke(true)} data-testid="revoke-grant">
            Revocar grant
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="motivo (opcional)"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              data-testid="revoke-reason"
              style={{ padding: '0.3rem', border: '1px solid var(--lumal-border)', borderRadius: 4 }}
            />
            <button style={btnDanger} onClick={() => revoke.mutate()} disabled={revoke.isPending} data-testid="revoke-confirm">
              Confirmar revocación
            </button>
            <button style={btnSecondary} onClick={() => setConfirmRevoke(false)}>Cancelar</button>
          </div>
        )}
      </div>

      <div style={{ marginTop: '0.6rem' }}>
        <button style={btnSecondary} onClick={() => setShowHistory((v) => !v)} data-testid="toggle-history">
          {showHistory ? 'Ocultar historial' : 'Ver historial del grant'}
        </button>
        {showHistory ? (
          <table style={tbl} data-testid="grant-history-table">
            <thead><tr><th>#</th><th>Cuándo</th><th>Quién</th><th>Tipo</th><th>Antes → Después (TTL)</th></tr></thead>
            <tbody>
              {(history.data?.items || []).map((c) => (
                <tr key={c.id} data-testid={`history-${c.seq}`}>
                  <td>{c.seq}</td>
                  <td>{new Date(c.changed_at).toLocaleString()}</td>
                  <td>{c.changed_by}</td>
                  <td>{c.change_type}</td>
                  <td>
                    {String(c.before_value?.expires_at ?? '')} → {String(c.after_value?.expires_at ?? '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
}

// TakeControl: "tomar control" — el CSE ejecuta una operación real del tenant. El
// formulario es mínimo (payload JSON editable); el enforcement vive en el core.
function TakeControl({
  csrf,
  grant,
  onDone,
}: {
  csrf: string;
  grant: Grant;
  onDone: () => void;
}) {
  // Sólo operaciones cuyo módulo Y acción estén en el scope del grant (o '*'). No
  // ofrecer ops fuera de scope evita el 403 OUT_OF_SCOPE_ACTION del core (bug b).
  const allowed = useMemo(
    () =>
      ACT_AS_OPS.filter(
        (op) =>
          (grant.scope_modules.includes('*') || grant.scope_modules.includes(op.module)) &&
          (grant.scope_actions.includes('*') || grant.scope_actions.includes(op.action)),
      ),
    [grant.scope_modules, grant.scope_actions],
  );
  const [opIdx, setOpIdx] = useState(0);
  const op = allowed[Math.min(opIdx, allowed.length - 1)];
  const [payload, setPayload] = useState(() => JSON.stringify(allowed[0]?.samplePayload ?? {}, null, 2));

  const run = useMutation({
    mutationFn: async () => {
      if (!op) throw { code: 'NO_OP', message_safe: 'sin operación seleccionada' };
      let parsed: unknown = {};
      try {
        parsed = JSON.parse(payload);
      } catch {
        throw { code: 'BAD_JSON', message_safe: 'payload JSON inválido' };
      }
      return apiFetch<unknown>(`${API}/portal/act-as`, 'POST', {
        headers: { 'X-CSRF-Token': csrf },
        body: {
          grant_id: grant.id,
          method: op.method,
          path: op.path,
          module: op.module,
          action: op.action,
          clinical: op.clinical ?? false,
          payload: parsed,
        },
      });
    },
    onSuccess: () => onDone(),
  });

  if (allowed.length === 0 || !op) {
    return (
      <div data-testid="take-control" style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--lumal-border)' }}>
        <h4 style={{ margin: '0 0 0.5rem' }}>Tomar control</h4>
        <Banner variant="info">El scope del grant no habilita operaciones de control.</Banner>
      </div>
    );
  }

  return (
    <div data-testid="take-control" style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--lumal-border)' }}>
      <h4 style={{ margin: '0 0 0.5rem' }}>Tomar control (operar como soporte)</h4>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8rem' }}>
        <select
          value={opIdx}
          onChange={(e) => {
            const i = Number(e.target.value);
            setOpIdx(i);
            setPayload(JSON.stringify(allowed[i]?.samplePayload ?? {}, null, 2));
          }}
          data-testid="act-op-select"
        >
          {allowed.map((o, i) => (
            <option key={o.label} value={i}>{o.label}</option>
          ))}
        </select>
        <span style={badge}>{op.method} {op.module}:{op.action}{op.clinical ? ' · clínico' : ''}</span>
      </div>
      <textarea
        value={payload}
        onChange={(e) => setPayload(e.target.value)}
        data-testid="act-payload"
        rows={5}
        style={{ width: '100%', marginTop: 6, fontFamily: 'monospace', fontSize: '0.78rem', border: '1px solid var(--lumal-border)', borderRadius: 4, padding: '0.4rem' }}
      />
      <div style={{ marginTop: 6 }}>
        <button style={btnPrimary} onClick={() => run.mutate()} disabled={run.isPending} data-testid="act-run">
          Ejecutar operación
        </button>
      </div>
      {run.isError ? (
        <Banner variant="warn">
          <span data-testid="act-error">
            Operación denegada: {(run.error as { code?: string })?.code ?? 'ERROR'} — {(run.error as { message_safe?: string })?.message_safe ?? ''}
          </span>
        </Banner>
      ) : null}
      {run.isSuccess ? <p data-testid="act-ok" style={{ fontSize: '0.82rem', color: 'var(--lumal-success)' }}>Operación ejecutada (auditada).</p> : null}
    </div>
  );
}

// useTtlRemaining cuenta el tiempo restante del grant hacia su expiración.
function useTtlRemaining(expiresAt: string): string {
  const expiry = useMemo(() => new Date(expiresAt).getTime(), [expiresAt]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = expiry - now;
  if (ms <= 0) return 'expirado';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const s = Math.floor((ms % 60000) / 1000);
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
}
